// ═══════════════════════════════════════════════════════════════════════════
//  THE SCRUTINEER'S OPERATOR PANEL — `#scrutineer/<ballotId>`.
//
//  The clickable face of the five administrative acts the worker already
//  performs: close → seal → count → declare, plus SIGN-RESULT — the scrutineer's
//  own attestation to the count they ran, lawful from the count onward and after
//  the declaration alike (the two are different people's acts on the same
//  figures and neither waits for the other). It drives ONE ballot forward, one
//  lawful next-act at a time, and after the count it renders the results sheet
//  from the count route's own aggregate — this is the FIRST real (non-fixture)
//  reader of `<ResultPage>`, and it reaches it through a grant-gated route, which
//  is the deployment condition the results README sets out.
//
//  ── ONE ACT AT A TIME, AND THE SERVER DECIDES WHETHER IT MAY ───────────────
//  The panel shows a single primary action for the step it believes the ballot is
//  at. It authorises nothing: every POST re-gates server-side to a scrutineer of
//  this sitting (SO 15.11), and a 409 `wrong_state` is the server correcting the
//  panel's belief. The stage is READ FROM THE BALLOT on mount — `GET
//  /api/results/:ballotId`, which answers the state on a 200 and on its 409 alike
//  — and then tracked from each act's own response. The panel asserts NOTHING
//  about the poll until that read comes back, and if it fails it says so: a step
//  assumed is a sentence an operator is told, and this panel used to open every
//  ballot at "Voting is open."
//
//  ── SEXTON'S INTERACTION LAW ───────────────────────────────────────────────
//  close, seal, declare and sign are CONSEQUENTIAL — a two-tap confirm stands in
//  front of each, in plain language, and Cancel is listed first (the confirming
//  press must not land where the primary button was). COUNT is re-runnable —
//  `count_run` is append-only, a second count is added beside the first, never a
//  replacement — so it has no destructive confirm.
//
//  ── NO FIGURE BEFORE THE COUNT ─────────────────────────────────────────────
//  Nothing on the panel is a tally, a turnout or a countdown (registrar ruling,
//  the same rule as the cast surface). The only numbers on this screen are on the
//  results sheet, AFTER the count, where they are the scrutineer's to read.
//
//  ── AND THE PANEL NEVER DECLARES ───────────────────────────────────────────
//  Before declaration the sheet reports the arithmetic and its state line says
//  nothing has been declared; after declaration the same sheet re-renders with
//  `state: 'declared'` and the state line says the President has declared it. The
//  panel adds NO verdict of its own — the sheet's own state logic is the whole of
//  it (results/ResultPage.tsx).
//
//  ── TWO LIMITS, STATED RATHER THAN HIDDEN ──────────────────────────────────
//   • A MOTION SHEET NEEDS THE MOTION'S OWN WORDS, and the count response now
//     carries them: migration 0016 added `instrument`, `mover` and `seconder` to
//     `ballot_item`, and `CountedSheetItem` carries the three on a motion the way
//     `candidates` rides a position. So a motion ballot renders the same sheet an
//     election does. What is still refused is a motion whose INSTRUMENT did not
//     come back — "a result nobody can read the motion of is not a report of
//     anything" (results/view.ts) — and that screen offers no Declare control
//     either, because a count nobody can read is a count nobody has verified
//     (see {@link buildResultView}).
//   • THE VIEWER IS 'scrutineer'. The count route admits scrutineers only, so the
//     operator who reached this sheet provably holds that role; the sheet's Admin
//     block (an informational note, no controls) is not shown to a dual-role
//     operator, which is a negligible loss and the least-privileged direction.
// ═══════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useId, useRef, useState, type JSX, type ReactNode } from 'react';
import { PortalHeader } from '@cca/ui';
import { useFocusOnMount } from '../a11y/useFocusOnMount';
import {
  api,
  DecidesApiError,
  type CountRunResult,
  type Identity,
  type ResultSignatureRecord,
} from '../api/client';
import { instrumentParagraphs } from '../paper';
import { portalNameFor } from '../portal-name';
import { ResultPage } from '../results/ResultPage';
import { RollIntegrityNotice } from '../results/RollIntegrityNotice';
import {
  ROLL_INTEGRITY_COPY,
  ROLL_INTEGRITY_NOTICE_ID,
  rollIntegrityFrom,
  type BallotSecrecy,
  type ResultBallotState,
  type ResultItemView,
  type ResultView,
  type Viewer,
} from '../results/view';

/**
 * The operator's own view of how far the ballot has come — SEEDED from the
 * ballot's own state on mount, then moved by each act. Still distinct from the DB
 * state: 'counted' is the operator holding a result while the ballot row is
 * unchanged at 'sealed' (the count does not move the ballot's state).
 *
 * THE FIRST TWO ARMS ARE THE HONEST ABSENCE OF A BELIEF, and they are why this
 * type is not simply the six words the schema's CHECK admits. 'reading' asserts
 * nothing while the read is in flight; 'unread' is a read that FAILED, and it is
 * a state of its own rather than a fallback to 'open', because assuming a step
 * the server did not confirm is the whole of the defect this replaced.
 */
type Stage =
  | { step: 'reading' }
  /** The state could not be read — refused, unreachable, or a word this build
   *  does not know. `code` picks the sentence ({@link ERROR_COPY}). */
  | { step: 'unread'; code: string }
  /** 'draft' or 'ready': a ballot that has NOT opened. No act on this panel is
   *  lawful on it — close/seal/count/declare each require a later state — and
   *  opening one is the Synod Admin's act on the setup hub, not a Scrutineer's. */
  | { step: 'unopened'; state: 'draft' | 'ready' }
  | { step: 'open' }
  | { step: 'closed' }
  | { step: 'sealed' }
  | { step: 'counted'; run: CountRunResult }
  | { step: 'declared'; run: CountRunResult }
  /**
   * DECLARED, WITH NO COUNT IN HAND — reached by reading a ballot whose row
   * already says 'declared', as against {@link Stage} 'declared', which is this
   * operator having just declared the count they are holding.
   *
   * It is a separate arm precisely BECAUSE it cannot carry a `run`: a state read
   * returns no arithmetic, and making `run` optional would let `<ResultPage>`
   * render a sheet with nothing on it. So this screen shows no sheet and offers
   * no control — none is lawful on a declared ballot in any case — and says so.
   */
  | { step: 'declared-elsewhere' };

/**
 * The ballot's own word for where it stands → the step this panel may believe.
 *
 * `null` for a word this build cannot name, and that is the safe direction: the
 * caller turns it into 'unread' rather than into a guess. The six words are
 * `ballot.state`'s CHECK ('draft', 'ready', 'open', 'closed', 'sealed',
 * 'declared'), narrowed HERE — at the boundary that reads them — because on the
 * wire the column is a plain string like every other CHECK-fenced one.
 */
function stageForState(state: string): Stage | null {
  switch (state) {
    case 'draft':
    case 'ready':
      return { step: 'unopened', state };
    case 'open':
      return { step: 'open' };
    case 'closed':
      return { step: 'closed' };
    case 'sealed':
      return { step: 'sealed' };
    case 'declared':
      return { step: 'declared-elsewhere' };
    default:
      return null;
  }
}

type Confirm = 'close' | 'seal' | 'declare' | 'sign' | null;

/**
 * Who has already signed the run in hand — the panel's own read of
 * `GET …/result-signatures/:countRunId`, with the same honest-absence shape as
 * {@link Stage}: 'reading' asserts nothing, and 'unread' is a read that FAILED,
 * kept distinct from an empty list because "nobody has signed" and "the list
 * could not be read" are different sentences. A failed read never withholds the
 * form: the list is a courtesy and not a fence (the worker's own header forbids
 * building refusal or permission on it), and a second signature is lawful.
 */
type SignaturesState =
  { step: 'reading' } | { step: 'unread' } | { step: 'read'; signatures: ResultSignatureRecord[] };

/** The count route admits scrutineers only (SO 15.11), so an operator holding a
 *  counted sheet is a scrutineer. See the header for why this is not derived from
 *  `identity` per sitting. */
const VIEWER: Viewer = 'scrutineer';

const NEXT_LABEL: Record<'open' | 'closed' | 'sealed', string> = {
  open: 'Close voting',
  closed: 'Seal the papers',
  sealed: 'Run the count',
};

const STEP_NOTE: Record<'open' | 'closed' | 'sealed', string> = {
  open: 'Voting is open. When Synod has finished, close it — members can no longer vote once it is closed.',
  closed: 'Voting is closed. Seal the papers to sever each one from the elector who cast it.',
  sealed:
    'The papers are sealed. Run the count to produce the result. The count may be run again — it is only ever added to, never replaced.',
};

/** A ballot that has not opened, in the two states it can be in before it does.
 *  Both say the same first thing — nobody can vote yet — and then differ only in
 *  how far the build has got, because that is the difference between "still being
 *  written" and "waiting for the Administrator to open it". Neither offers an act:
 *  every act on this panel needs a later state, and opening is not a Scrutineer's
 *  to do. */
const UNOPENED_NOTE: Record<'draft' | 'ready', string> = {
  draft:
    'Voting has not opened, and nobody can vote on this ballot yet — it is still being built. There is nothing to close, seal or count.',
  ready:
    'Voting has not opened, and nobody can vote on this ballot yet — it is built and marked ready, and waiting for the Synod Admin to open it. There is nothing to close, seal or count.',
};

/** Where the operator goes instead. Named after the link that carries them there,
 *  so the sentence and the landing agree word for word — this panel knows the
 *  BALLOT's id and not the sitting's, so it cannot link to the hub itself. */
const SETUP_POINTER =
  'Go back to your sittings and open “Set up the roll and ballots” for this sitting. Opening a ballot is the Synod Admin’s act.';

const CONFIRM_COPY: Record<
  'close' | 'seal' | 'declare' | 'sign',
  { prompt: string; confirmLabel: string }
> = {
  close: {
    prompt: 'Close voting now? Members can no longer vote once voting is closed.',
    confirmLabel: 'Yes, close voting',
  },
  seal: {
    prompt:
      'Seal the papers now? This severs every paper from the elector who cast it, and cannot be undone.',
    confirmLabel: 'Yes, seal the papers',
  },
  declare: {
    prompt: 'Declare this result now? This records that the President has declared it to Synod.',
    confirmLabel: 'Yes, declare the result',
  },
  sign: {
    prompt:
      'Sign this result now? Your name and the time are recorded permanently, and a signature cannot be withdrawn.',
    confirmLabel: 'Yes, sign this result',
  },
};

/** Wire codes → sentences an operator can act on. Every one ends by saying
 *  whether anything changed, because that is what the operator needs first. */
const ERROR_COPY: Record<string, string> = {
  unauthorized: 'Your session has ended. Sign in again — nothing was changed.',
  not_found: 'There is no ballot with that identifier. Nothing was changed.',
  no_grant:
    'You do not hold a scrutineer role for this sitting, so you cannot run these steps. Nothing was changed.',
  wrong_state:
    'This ballot is not at the step this panel expected — it may already have moved on, or another scrutineer may be acting on it. Go back and open the panel again to pick it up where it is.',
  missing_declaration: 'The President’s name and the counted result are both needed to declare.',
  declaration_precondition:
    'This result cannot be declared: the count named for it is not one that may be declared against. Run the count again and declare that.',
  // ── The two refusals the SIGN act can earn, both 422s ─────────────────────
  missing_signature:
    'A signature needs your full name. Nothing was recorded — type your name and sign again.',
  unknown_count_run:
    'The count this signature names is not one of this ballot’s own, so nothing was recorded. Run the count again and sign the fresh sheet.',
  conflict: 'Another change reached this ballot first. Go back and open the panel again.',
  unreachable: 'The ballot could not be reached. Nothing was changed — try again in a moment.',
  // ── The two IDENTITY refusals, which the state read made ordinary ─────────
  // Both are `resolveViewer`'s (`@cca/decides-worker/src/grants.ts`) and both
  // arrive as a 403. They were reachable before this panel read the state — every
  // act runs the same gate — but the read is now the FIRST thing it does, so the
  // fallback sentence is no longer good enough for either. NEITHER IS THE
  // READER'S FAULT: one address on two Contacts records, or on none, is a record
  // to correct, and copy that implied otherwise would send an operator hunting
  // their own account in the middle of a sitting.
  unknown_identity:
    'The address you signed in with is not matched to anybody on this sitting. Nothing was changed — speak to the registrar, who can correct the record.',
  ambiguous_identity:
    'The address you signed in with points to more than one person, so nobody can be served on it. Nothing was changed, and this is a record to correct rather than anything you did — the registrar can give each of them their own address.',
  // ── The two the STATE READ can meet, and neither has an act behind it ──────
  // A ballot state this build cannot name. It is the read's answer to a word from
  // outside the six the schema admits — a newer worker, or a row edited by hand —
  // and the panel refuses to map it onto a step rather than picking the nearest
  // one. Nothing was changed because nothing was attempted.
  unknown_state:
    'This ballot reports a state this panel does not know, so it will not guess at which step it is at. Nothing was changed — try again, and tell the registrar if it says this a second time.',
  // `GET /api/results` answers this 409 for a ballot that has not been closed, and
  // it normally carries the state alongside — which is the whole read. Reaching
  // this sentence means the state did NOT come with it, which is a worker older
  // than this bundle rather than anything about the ballot.
  ballot_not_counted:
    'This ballot has not been counted, and it did not say which step it is at. Nothing was changed — try again in a moment.',
};

const errorSentence = (code: string): string =>
  ERROR_COPY[code] ?? 'That step could not be completed. Nothing was changed.';

/**
 * The count's instant in NZ words. Formatted HERE rather than in the sheet: a
 * component that formats an instant has to pick a zone, and the one that is right
 * is Pacific/Auckland (House Rules — NZ time everywhere), never the browser's.
 */
function formatNzInstant(utcIso: string): string {
  const when = new Date(utcIso);
  if (Number.isNaN(when.getTime())) return utcIso;
  const formatted = new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(when);
  return `${formatted} (NZ)`;
}

/** The zoneless NZ wall clock the worker's schedule surface speaks: exactly
 *  sixteen characters, `YYYY-MM-DDTHH:MM`. No seconds, no offset, no `Z`. */
const NZ_WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
] as const;

/**
 * One wall clock in words — "7:30pm on 19 September 2026".
 *
 * **NOT {@link formatNzInstant}, and it must not become it.** That one takes a
 * UTC instant and renders it in Pacific/Auckland, which is right for an instant
 * and wrong here: `closesAtNz` is ALREADY NZ wall clock with no instant behind
 * it, so handing it to `new Date()` would read it in the browser's zone and then
 * shift it into NZ — moving the registrar's close by however far this machine is
 * from Auckland. Pure string work; falls back to the raw value for anything it
 * cannot read.
 *
 * A SECOND HONEST COPY of setup/SchedulePanel.tsx's `wallClockInWords`, on the
 * setup ConfirmBlock's own reasoning: that one is private to its surface, and
 * importing across the two to share thirty lines is how a shared thing starts
 * being widened for one caller.
 */
function wallClockInWords(value: string): string {
  if (!NZ_WALL_CLOCK.test(value)) return value;
  const month = MONTHS[Number(value.slice(5, 7)) - 1];
  if (month === undefined) return value;
  const day = Number(value.slice(8, 10));
  const year = value.slice(0, 4);
  const hour24 = Number(value.slice(11, 13));
  const minute = value.slice(14, 16);
  const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
  const half = hour24 < 12 ? 'am' : 'pm';
  return `${String(hour12)}:${minute}${half} on ${String(day)} ${month} ${year}`;
}

/**
 * THE ATTESTATION SENTENCE — fixed copy, shown beside the name field and sent
 * VERBATIM as `signatureText`, built in this one function so what was shown is
 * what is stored (the worker keeps it word for word in the append-only
 * `e_sig_audit` row). The estate's e-sig wording shape, with the run id inside
 * the sentence so the words themselves name WHICH count was attested.
 */
function attestationSentence(countRunId: string): string {
  return `I confirm that pressing Sign constitutes my electronic signature, and that the figures on this sheet are the result of count ${countRunId} which I conducted.`;
}

/**
 * A caption the record actually holds, or nothing. Whitespace is not a name, and
 * an absent one draws no row on the sheet — the same fold the cast screen makes
 * (`nameOrNull`, cast/CastPage.tsx), so the paper a member marked and the report
 * of it treat a blank the same way. `undefined` (the field never arrived) and
 * `null` (the column is empty) are one answer here: neither is a name.
 */
function recordedName(value: string | null | undefined): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

/**
 * Assemble a real {@link ResultView} from the count response — the count verbatim
 * plus the sheet's display words — or `null` when the sheet cannot be built here.
 *
 * **BOTH KINDS OF ITEM ARE BUILT.** A motion sheet needs the motion's own words,
 * and the count response carries them (`CountedSheetItem`'s `instrument`, `mover`
 * and `seconder`, from the columns migration 0016 added). A motion ballot used to
 * return `null` here on the ground that the schema stored none of the three; that
 * ceased to be true, and the note it produced left a scrutineer able to close,
 * seal, count and declare a constitutional amendment without ever being shown the
 * arithmetic.
 *
 * **`null` NOW MEANS ONE THING: a motion whose INSTRUMENT did not come back** —
 * absent from the sheet metadata, or present and empty once split. It is the one
 * field a motion sheet cannot be assembled without ("a result nobody can read the
 * motion of is not a report of anything", results/view.ts), and the emptiness test
 * is `instrumentParagraphs`' own — the SAME call the render makes, so the gate and
 * the sheet cannot come to disagree about what "empty" means. A missing mover or
 * seconder is NOT that case: it is a lawful motion, and the row is simply not
 * drawn.
 *
 * A missing TITLE is not that case either. It falls back to `''` for a motion
 * exactly as it already did for a position: the instrument is what a reader checks
 * the count against, and an item is not un-reportable for want of its agenda line.
 *
 * On a fatal count `result.items` is empty, so the loop does not run and a valid
 * view is returned for the sheet's own FatalNotice to render — the fatal case
 * therefore never reaches the `null` road.
 */
function buildResultView(
  payload: CountRunResult,
  viewer: Viewer,
  state: ResultBallotState,
): ResultView | null {
  const meta = new Map(payload.sheet.items.map((item) => [item.id, item] as const));
  const items: ResultItemView[] = [];
  for (const item of payload.result.items) {
    const display = meta.get(item.itemId);
    if (item.kind === 'motion') {
      const instrument = display?.instrument ?? '';
      if (instrumentParagraphs(instrument).length === 0) return null;
      items.push({
        kind: 'motion',
        result: item,
        title: display?.title ?? '',
        instrument,
        mover: recordedName(display?.mover),
        seconder: recordedName(display?.seconder),
      });
      continue;
    }
    const candidateNames: Record<string, string> = {};
    for (const candidate of display?.candidates ?? []) {
      candidateNames[candidate.id] = candidate.displayName;
    }
    items.push({ kind: 'position', result: item, title: display?.title ?? '', candidateNames });
  }
  // The wire's CHECK-fenced word, narrowed at this boundary. `secret` is the safe
  // fallback for a word this build cannot name (it withholds more), and elections
  // are structurally always secret in any case.
  const secrecy: BallotSecrecy =
    payload.sheet.secrecy === 'attributable' ? 'attributable' : 'secret';
  return {
    result: payload.result,
    ballot: { title: payload.sheet.ballotName, state, secrecy },
    items,
    provenance: {
      countRunId: payload.countRunId,
      engineVersion: payload.sheet.engineVersion,
      definitionDigest: payload.sheet.definitionDigest,
      resultDigest: payload.sheet.resultDigest,
      runAtNz: formatNzInstant(payload.sheet.runAtUtc),
      rollDigest: payload.sheet.rollDigest,
      rollAmendmentSeq: payload.sheet.rollAmendmentSeq,
    },
    // The box, the roll and the window, carried whole so the sheet can state
    // them (view.ts holds the sentences). TWO time treatments, deliberately:
    // the seal stamp and an extension's instant are UTC INSTANTS and go through
    // formatNzInstant; `closesAtNz` is the registrar's own zoneless wall clock
    // and goes through pure string work — see wallClockInWords for why the
    // other road would move the close by this machine's distance from Auckland.
    coverage: {
      papersSealedCount: payload.sheet.papersSealedCount,
      papersSealedAtNz:
        payload.sheet.papersSealedAtUtc === null
          ? null
          : formatNzInstant(payload.sheet.papersSealedAtUtc),
      summonedClergy: payload.sheet.summonedClergy,
      summonedLaity: payload.sheet.summonedLaity,
      summonedBishop: payload.sheet.summonedBishop,
      closesAtNz:
        payload.sheet.closesAtNz === null
          ? null
          : `${wallClockInWords(payload.sheet.closesAtNz)} (NZ)`,
      extensions: payload.sheet.extensions.map((extension) => ({
        occurredAtNz: formatNzInstant(extension.occurredAtUtc),
        reason: extension.reason,
      })),
    },
    // The marks-roll check the count route ran beside THIS count — read off the
    // same response, so the sheet and its check describe one moment.
    rollIntegrity: rollIntegrityFrom(payload.rollIntegrity),
    viewer,
  };
}

interface Props {
  ballotId: string;
  /** Read for the header's name only. It authorises nothing: every act re-resolves
   *  standing server-side on the request that runs it. */
  identity: Identity;
  onBack: () => void;
}

export function ScrutineerPage({ ballotId, identity, onBack }: Props): JSX.Element {
  const [stage, setStage] = useState<Stage>({ step: 'reading' });
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Announced once per transition (a stage word, never a tally) — see the
   *  {@link Announce} live region below. */
  const [status, setStatus] = useState<string | null>(null);
  const [president, setPresident] = useState('');
  /** The scrutineer's own name as typed into the signing form — cleared after a
   *  signature lands, so the form returns to rest under the grown list. */
  const [signerName, setSignerName] = useState('');
  const [signatures, setSignatures] = useState<SignaturesState>({ step: 'reading' });
  /** Names the counted panel's region landmark from its own heading — the same
   *  heading focus lands on once the count is in. */
  const panelHeadingId = useId();
  /** The SENTENCE (not the whole line, whose link would be read twice) under
   *  that heading which points at the sheet's roll check — the heading is
   *  described by it, so focus landing there announces it too. */
  const rollPointerId = useId();

  /**
   * ASK THE BALLOT WHERE IT STANDS, before the panel offers anything.
   *
   * The state arrives on EITHER response class and is read the same way from both:
   * a 200 carries `ballot.state`, and the 409 this route answers for a ballot that
   * has not been closed carries `state` beside its code
   * ({@link DecidesApiError.state}). So one call covers all six states, and
   * `stageForState` is the only thing that turns a word into a step.
   *
   * **EVERY OTHER ROAD OUT OF HERE IS 'unread', AND NONE OF THEM IS 'open'.** A
   * refusal (401/403/404), a request that reached no server, a state word this
   * build does not know, a 409 that arrived without one, and a 200 whose body is
   * not the shape this reads — the last needs no arm of its own, because the
   * property access throws inside this same `try` and lands on the non-refusal
   * road. Each ends on a screen that says the state could not be read and offers a
   * retry. Falling back to a step would put a sentence about the poll in front of
   * an operator on the strength of a failed read, which is the defect this read
   * was written to end.
   */
  const readState = useCallback(async (): Promise<void> => {
    setStage({ step: 'reading' });
    setError(null);
    try {
      const read = await api.results(ballotId);
      setStage(stageForState(read.ballot.state) ?? { step: 'unread', code: 'unknown_state' });
    } catch (err) {
      if (!(err instanceof DecidesApiError)) {
        setStage({ step: 'unread', code: 'unreachable' });
        return;
      }
      // A refusal that carried no state says only why it refused; one that carried
      // a state this build cannot name is a different fault and says so.
      if (err.state === null) {
        setStage({ step: 'unread', code: err.code });
        return;
      }
      setStage(stageForState(err.state) ?? { step: 'unread', code: 'unknown_state' });
    }
  }, [ballotId]);

  useEffect(() => {
    void readState();
  }, [readState]);

  /**
   * The run whose signatures the panel shows — null when no run is in hand
   * (pre-count steps, or a ballot declared elsewhere: a state read carries no
   * run id, so there is nothing to ask about) and null on a FATAL count, which
   * produced no figures and gets no signing surface, for the same reason it
   * gets no Declare (the whole of a signature is "the figures on this sheet").
   */
  const signableRunId =
    (stage.step === 'counted' || stage.step === 'declared') && stage.run.result.status !== 'fatal'
      ? stage.run.countRunId
      : null;

  // ASK WHO HAS ALREADY SIGNED, the moment a run is in hand — BEFORE the form is
  // usable, so nobody signs twice by accident. Re-runs when a fresh count mints
  // a fresh run id (the list belongs to ONE run). A failure lands on 'unread',
  // its own sentence, never on an empty list — an empty list means "nobody has
  // signed" and must not be reachable by a network error.
  useEffect(() => {
    if (signableRunId === null) return;
    let cancelled = false;
    setSignatures({ step: 'reading' });
    void api
      .resultSignatures(ballotId, signableRunId)
      .then((read) => {
        if (cancelled) return;
        // readState's own discipline for a 200 whose body is not this shape:
        // the property access throws HERE, inside the chain, and lands on the
        // failure road below — never stored to blow up mid-render. `.slice()`
        // is that access: it proves an array or it throws.
        setSignatures({ step: 'read', signatures: read.signatures.slice() });
      })
      .catch(() => {
        if (!cancelled) setSignatures({ step: 'unread' });
      });
    return () => {
      cancelled = true;
    };
  }, [ballotId, signableRunId]);

  /** Run one act with the shared busy/error lifecycle. The act sets its own stage
   *  on success; a refusal throws before it does, so the stage never advances past
   *  a step the server refused, and the code is stored for display. */
  const perform = useCallback(async (run: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await run();
    } catch (err) {
      setError(err instanceof DecidesApiError ? err.code : 'unreachable');
    } finally {
      setBusy(false);
    }
  }, []);

  const doClose = useCallback(
    () =>
      perform(async () => {
        await api.close(ballotId);
        setConfirm(null);
        setStatus('Voting is closed. Members can no longer vote.');
        setStage({ step: 'closed' });
      }),
    [ballotId, perform],
  );

  const doSeal = useCallback(
    () =>
      perform(async () => {
        await api.seal(ballotId);
        setConfirm(null);
        setStatus('The papers are sealed.');
        setStage({ step: 'sealed' });
      }),
    [ballotId, perform],
  );

  const doCount = useCallback(
    () =>
      perform(async () => {
        const run = await api.count(ballotId);
        setStatus('The count is complete.');
        setStage({ step: 'counted', run });
      }),
    [ballotId, perform],
  );

  const doDeclare = useCallback(
    () =>
      perform(async () => {
        if (stage.step !== 'counted') return;
        await api.declare(ballotId, {
          president: president.trim(),
          countRunId: stage.run.countRunId,
        });
        setConfirm(null);
        setStatus('The result is declared.');
        setStage({ step: 'declared', run: stage.run });
      }),
    [ballotId, perform, stage, president],
  );

  /** Sign the sheet. The sentence sent is EXACTLY the one the form displayed —
   *  both come from {@link attestationSentence} over the same run id — and the
   *  list is redrawn from the response, never appended to optimistically. On a
   *  refusal `perform` stores the code and the panel stays put. */
  const doSign = useCallback(
    () =>
      perform(async () => {
        if (stage.step !== 'counted' && stage.step !== 'declared') return;
        const runId = stage.run.countRunId;
        const signed = await api.signResult(ballotId, {
          countRunId: runId,
          signerName: signerName.trim(),
          signatureText: attestationSentence(runId),
        });
        setConfirm(null);
        setSignerName('');
        setStatus('The result is signed.');
        // `.slice()` for the same reason as the read effect: prove the array
        // inside the act, where a skewed worker's malformed 200 lands in
        // `perform`'s catch instead of blowing up mid-render.
        setSignatures({ step: 'read', signatures: signed.signatures.slice() });
      }),
    [ballotId, perform, stage, signerName],
  );

  // ── Before the panel believes anything: the state read ────────────────────
  // `role="status"` so the wait is announced rather than being a silently blank
  // page (the shell's own device for a session it is re-checking). There is no
  // step note and no primary control here, deliberately: this screen is the panel
  // NOT saying where the ballot is at, which is the honest thing to render while
  // it does not know.
  if (stage.step === 'reading') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">Running the ballot</h1>
        <p role="status">Reading where this ballot stands…</p>
      </Frame>
    );
  }

  // THE READ FAILED, AND THAT IS WHAT IS SAID. The heading covers a refusal AND an
  // unreachable server, because at this point the screen does not always know
  // which it met — and there is no act offered at all, because every act on this
  // panel is consequential and the step it would belong to is exactly what is not
  // known. "Try again" re-reads the state; it changes nothing either way.
  if (stage.step === 'unread') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">This ballot’s state could not be read</h1>
        <p className="alert" role="alert">
          {errorSentence(stage.code)}
        </p>
        <p className="decides-scrutineer-stepnote">
          Nothing is offered here until this panel can read where the ballot stands — closing or
          sealing the wrong ballot cannot be undone.
        </p>
        <button type="button" className="btn btn-ghost" onClick={() => void readState()}>
          Try again
        </button>
      </Frame>
    );
  }

  // NOT YET OPENED — 'draft' or 'ready'. No act on this panel is lawful, so none
  // is drawn: the operator is told plainly that nobody can vote yet, and pointed
  // at the surface where opening actually happens.
  if (stage.step === 'unopened') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">Voting has not opened</h1>
        <section className="decides-scrutineer-step card">
          <p className="decides-scrutineer-stepnote">{UNOPENED_NOTE[stage.state]}</p>
          <p className="decides-scrutineer-stepnote">{SETUP_POINTER}</p>
        </section>
      </Frame>
    );
  }

  // ALREADY DECLARED, WITH NO COUNT IN HAND. Nothing is offered because nothing is
  // lawful — close, seal, count and declare each refuse a declared ballot — and no
  // sheet is drawn because a state read carries no arithmetic and a fabricated one
  // would be worse than none. The count that WAS declared is on the ballot's
  // record; this panel shows a sheet only for a count it ran itself.
  if (stage.step === 'declared-elsewhere') {
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">The result is declared</h1>
        <section className="decides-scrutineer-step card">
          <p className="decides-scrutineer-stepnote">
            The President has declared this result to Synod, so this ballot is finished: there is
            nothing left to close, seal or declare.
          </p>
          <p className="decides-scrutineer-stepnote">
            The declared count is on the ballot’s own record. This panel renders the results sheet
            only from a count it ran itself, and it has run none in this session, so there is no
            sheet to show you here.
          </p>
        </section>
      </Frame>
    );
  }

  // ── The pre-count steps: open → closed → sealed, each a single next-act ────
  if (stage.step === 'open' || stage.step === 'closed' || stage.step === 'sealed') {
    const step = stage.step;
    const pending: Confirm = step === 'open' ? 'close' : step === 'closed' ? 'seal' : null;
    return (
      <Frame identity={identity} onBack={onBack}>
        <h1 className="ballot-title">Running the ballot</h1>
        <p className="decides-scrutineer-lead">
          Take the ballot through its steps one at a time. Nothing on this screen is a tally or a
          turnout, and nothing is declared here — under Standing Order 14.1(k) the President
          declares the result to Synod.
        </p>
        <Announce status={status} />
        {error !== null ? (
          <p className="alert" role="alert">
            {errorSentence(error)}
          </p>
        ) : null}
        <section className="decides-scrutineer-step card">
          <p className="decides-scrutineer-stepnote">{STEP_NOTE[step]}</p>
          {confirm !== null && confirm === pending ? (
            <ConfirmBlock
              prompt={CONFIRM_COPY[confirm].prompt}
              confirmLabel={CONFIRM_COPY[confirm].confirmLabel}
              busy={busy}
              onConfirm={() => {
                if (confirm === 'close') void doClose();
                else if (confirm === 'seal') void doSeal();
              }}
              onCancel={() => setConfirm(null)}
            />
          ) : (
            <button
              type="button"
              className="btn btn-primary decides-scrutineer-primary"
              disabled={busy}
              onClick={() => {
                if (step === 'sealed') void doCount();
                else setConfirm(pending);
              }}
            >
              {NEXT_LABEL[step]}
            </button>
          )}
        </section>
      </Frame>
    );
  }

  // ── Post-count: the results sheet the count produced, THEN the operator
  //    controls. The sheet is rendered first so the estate PortalHeader stays
  //    top-of-page and, in rehearsal, the Declare control sits BELOW the "not a
  //    live ballot" band instead of above it. ──────────────────────────────────
  const state: ResultBallotState = stage.step === 'declared' ? 'declared' : 'sealed';
  const view = buildResultView(stage.run, VIEWER, state);
  const fatal = stage.run.result.status === 'fatal';

  // The post-count heading, and the count-complete announcement: focus is moved
  // to it on mount (see {@link CountedHeading}) because the "Run the count"
  // button has unmounted and the freshly-mounted live region below is not
  // reliably spoken. A stage word, never a figure.
  const headingText =
    stage.step === 'declared'
      ? 'The result is declared.'
      : fatal
        ? 'The count did not run.'
        : 'The count is complete.';

  // What each act reported, with no control attached — the live region and the
  // last refusal. Shown on BOTH post-count screens, because an operator who
  // cannot be offered the next act still has to be told what the last one did.
  const feedback = (
    <>
      <Announce status={status} />
      {error !== null ? (
        <p className="alert" role="alert">
          {errorSentence(error)}
        </p>
      ) : null}
    </>
  );

  // The next-act controls, WITHOUT the feedback: on the counted panel the
  // feedback renders once, up with the heading, so a refusal from ANY act — a
  // declare and a signature share this panel now — is said in one place.
  const controls =
    stage.step === 'declared' ? (
      <p className="decides-scrutineer-done">
        The result is declared. This page reports the count the declaration was made from.
      </p>
    ) : fatal ? (
      <p className="alert" role="alert">
        The count did not run, so there is nothing to declare. What stopped it is set out on the
        sheet above.
      </p>
    ) : (
      <DeclareForm
        president={president}
        onPresident={setPresident}
        confirming={confirm === 'declare'}
        busy={busy}
        onStart={() => setConfirm('declare')}
        onCancel={() => setConfirm(null)}
        onConfirm={() => void doDeclare()}
      />
    );

  // NO SHEET COULD BE ASSEMBLED — a motion whose instrument did not come back
  // with the count (see buildResultView). There is no <ResultPage>, so <Frame>'s
  // own <main> carries the heading, what the last act reported, and a note. The
  // heading takes focus on mount, the same as the counted panel.
  //
  // AND THERE IS NO DECLARE CONTROL HERE, deliberately. The whole of the
  // scrutineer's job is to verify the count before the President declares it
  // (SO 14.1(k)), and this screen shows no arithmetic at all — a Declare button
  // under a note saying the sheet is unavailable invites exactly the declaration
  // nobody has checked. The count itself is unharmed and re-runnable; what is
  // missing is the record of the motion, which is a person's to repair.
  //
  // THE MARKS-ROLL CHECK IS SAID HERE TOO. It rides the same count response as
  // the sheet that could not be built, and this is the one post-count screen with
  // no <ResultPage> to say it — so without this line a finding would reach every
  // counted screen but this one.
  if (view === null) {
    return (
      <Frame identity={identity} onBack={onBack}>
        <CountedHeading>{headingText}</CountedHeading>
        {feedback}
        <p className="alert" role="alert">
          The count ran, but the words of the motion did not come back with it, so there is no
          results sheet to read — and nothing may be declared from a count nobody can check. Run the
          count again; if the motion’s words are still missing, the ballot’s own record needs a
          person before anything is declared.
        </p>
        <RollIntegrityNotice integrity={rollIntegrityFrom(stage.run.rollIntegrity)} />
      </Frame>
    );
  }

  // Whether the sheet carries a marks-roll note the operator must be pointed at:
  // anything but the clean answer, which draws no note (see RollNotePointer).
  const rollNote = view.rollIntegrity.state !== 'clean';

  return (
    <>
      <ResultPage view={view} />
      {/* The operator controls follow the sheet, in their own labelled region:
          the sheet's <main> is the page's single main landmark, so a second
          <main> here would be a duplicate. The heading names the region and is
          where focus lands once the count is in.

          The `decides-scrutineer-operator` wrappers are the PRINT seam: the
          printed page is the sheet plus the signature record, so everything
          that is an operator's control or transient feedback sits inside one of
          the two wrappers and print.css hides them by that one class, rather
          than by a list of controls that undercounts by one. */}
      <section className="decides-scrutineer-panel" aria-labelledby={panelHeadingId}>
        <div className="page-narrow">
          <div className="decides-scrutineer-operator">
            <button
              type="button"
              className="btn btn-ghost decides-scrutineer-back"
              onClick={onBack}
            >
              Back to your sittings
            </button>
            <CountedHeading id={panelHeadingId} describedBy={rollNote ? rollPointerId : undefined}>
              {headingText}
            </CountedHeading>
            {rollNote ? <RollNotePointer id={rollPointerId} /> : null}
            {feedback}
          </div>
          {/* The signature record and the pen — BETWEEN the sheet and the
              declare control, and absent entirely on a fatal count, which has
              no figures for a signature's sentence to be about. */}
          {signableRunId !== null ? (
            <SignatureBlock
              countRunId={signableRunId}
              signatures={signatures}
              signerName={signerName}
              onSignerName={setSignerName}
              confirming={confirm === 'sign'}
              busy={busy}
              onStart={() => setConfirm('sign')}
              onCancel={() => setConfirm(null)}
              onConfirm={() => void doSign()}
            />
          ) : null}
          <div className="decides-scrutineer-operator">{controls}</div>
        </div>
      </section>
    </>
  );
}

/** The page frame the pre-count screens and the no-sheet note share — the estate
 *  header and this app's page column, matching the cast and register screens. */
function Frame({
  children,
  identity,
  onBack,
}: {
  children: ReactNode;
  identity: Identity;
  onBack: () => void;
}): JSX.Element {
  return (
    <div className="ballot-app min-dvh">
      <PortalHeader portalName={portalNameFor(identity)} />
      <main className="ballot-main">
        <div className="page-narrow">
          <button type="button" className="btn btn-ghost ballot-back" onClick={onBack}>
            Back to your sittings
          </button>
          {children}
        </div>
      </main>
    </div>
  );
}

/** The single live region for stage transitions. Always present so a change is
 *  announced; it carries a stage WORD and never a tally. */
function Announce({ status }: { status: string | null }): JSX.Element {
  return (
    <p className="decides-scrutineer-status" role="status">
      {status ?? ''}
    </p>
  );
}

/**
 * A post-count heading that takes focus when it mounts — the same shape as
 * CastPage's `Recorded`: a `tabIndex={-1}` heading and {@link useFocusOnMount}.
 * The count-complete transition unmounts the "Run the count" button and mounts a
 * fresh live region, which several screen readers do not speak on its first
 * message, so focus is moved here and the heading is the announcement. It
 * carries a stage word only, never a figure.
 *
 * `id` lets the counted panel name its region landmark from this same heading.
 * `describedBy` points at the SENTENCE under it that says the sheet reports a
 * roll check — the sentence alone, not the line with its link, so the
 * announcement on focus includes it once (cca-5uma).
 */
function CountedHeading({
  id,
  describedBy,
  children,
}: {
  id?: string;
  describedBy?: string | undefined;
  children: ReactNode;
}): JSX.Element {
  const headingRef = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(headingRef);
  return (
    <h1
      className="ballot-title"
      id={id}
      aria-describedby={describedBy}
      tabIndex={-1}
      ref={headingRef}
    >
      {children}
    </h1>
  );
}

/**
 * THE POINTER TO THE ROLL CHECK — one plain line under the counted panel's
 * heading, drawn only when the check is NOT clean (Miranda, cca-5uma).
 *
 * WHY IT EXISTS: focus lands on that heading once the count is in, and the
 * heading comes AFTER the whole sheet — so a screen-reader scrutineer reading
 * forward from there meets Sign and Declare and never the notice at the top of
 * the sheet. This line says the roll check is there, and its link takes focus
 * to it. It names it "the roll check" because that is how the notice opens
 * ("Roll check:"), so the words a person hears here are the words they land on.
 *
 * THE HEADING IS DESCRIBED BY THE SENTENCE ALONE — `id` is on the inner span,
 * not on the paragraph — so a screen reader announcing the heading reads the
 * sentence once and does not also read the link's text into it; the link is
 * then met in its own right.
 *
 * THE LINK IS NOT A PLAIN FRAGMENT LINK, and must not become one: this app
 * routes on `location.hash` (`#scrutineer/<ballotId>`, App.tsx), so following
 * `#<notice id>` would navigate away from the panel. The click is prevented and
 * focus is moved by hand to the notice, which carries `tabIndex={-1}` for
 * exactly this. The `href` is there so the element IS a link — focusable, and
 * announced as one — and it does NOT work anywhere else: opened in a new tab or
 * window (a middle-click, say), `#result-roll-integrity-note` is a fragment
 * App.tsx does not route, and the sheet would not be there in any case — its
 * figures exist only in THIS tab's count response.
 *
 * It sits inside `.decides-scrutineer-operator`, so print hides it — the paper
 * carries the notice itself. No number, nothing about the result, and nothing
 * about signing: it says where the roll check is and nothing more.
 */
function RollNotePointer({ id }: { id: string }): JSX.Element {
  return (
    <p className="decides-scrutineer-stepnote decides-scrutineer-roll-pointer">
      <span id={id}>{ROLL_INTEGRITY_COPY.pointer}</span>{' '}
      <a
        href={`#${ROLL_INTEGRITY_NOTICE_ID}`}
        onClick={(event) => {
          event.preventDefault();
          document.getElementById(ROLL_INTEGRITY_NOTICE_ID)?.focus();
        }}
      >
        {ROLL_INTEGRITY_COPY.pointerLink}
      </a>
    </p>
  );
}

/** Sexton's second tap. Cancel first — the confirming press must not land where
 *  the primary button was on the screen it replaced.
 *
 *  The confirm REPLACES that primary button, so the control that had focus
 *  unmounts. Focus is moved to the prompt — a heading with `tabIndex={-1}`, via
 *  {@link useFocusOnMount} — so the destructive question is spoken and the
 *  keyboard lands inside the confirm rather than on `<body>`. */
function ConfirmBlock({
  prompt,
  confirmLabel,
  busy,
  onConfirm,
  onCancel,
}: {
  prompt: string;
  confirmLabel: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): JSX.Element {
  const promptRef = useRef<HTMLHeadingElement>(null);
  useFocusOnMount(promptRef);
  return (
    <div className="decides-scrutineer-confirm" role="group" aria-label="Confirm this step">
      <h2 className="decides-scrutineer-prompt" tabIndex={-1} ref={promptRef}>
        {prompt}
      </h2>
      <div className="decides-scrutineer-actions">
        <button type="button" className="btn btn-ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-primary decides-scrutineer-primary"
          onClick={onConfirm}
          disabled={busy}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

/** The declaration step — a name for the President who declares, then the same
 *  two-tap confirm. The primary is disabled until a name is entered, so the empty
 *  case never reaches the server's `missing_declaration`. */
function DeclareForm({
  president,
  onPresident,
  confirming,
  busy,
  onStart,
  onCancel,
  onConfirm,
}: {
  president: string;
  onPresident: (value: string) => void;
  confirming: boolean;
  busy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const inputId = useId();
  return (
    <div className="decides-scrutineer-declare card">
      <label className="decides-scrutineer-label" htmlFor={inputId}>
        The President who declares this result
      </label>
      <input
        id={inputId}
        className="decides-scrutineer-input"
        type="text"
        value={president}
        onChange={(event) => onPresident(event.target.value)}
        disabled={confirming || busy}
        autoComplete="off"
      />
      {confirming ? (
        <ConfirmBlock
          prompt={CONFIRM_COPY.declare.prompt}
          confirmLabel={CONFIRM_COPY.declare.confirmLabel}
          busy={busy}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : (
        <button
          type="button"
          className="btn btn-primary decides-scrutineer-primary"
          onClick={onStart}
          disabled={president.trim() === '' || busy}
        >
          Declare the result
        </button>
      )}
    </div>
  );
}

/**
 * THE SIGNATURE RECORD AND THE PEN — who has already signed this run, said
 * PROMINENTLY BEFORE the form so nobody signs twice by accident, and then the
 * form itself, because a second signature is lawful (a synod has more than one
 * scrutineer) and is therefore shown, never prevented. The registrar's scope,
 * verbatim: "a straightforward Scrutineer confirmation process with a printed
 * signed result deliverable to the President of Synod" — so the block ends with
 * the print control and, on paper only, the two ruled countersign lines.
 *
 * IN PRINT this block IS the record: the heading, the list (or the honest
 * "nobody has signed" line) and the ruled lines survive; the form, the buttons
 * and the transient read-state lines do not (print.css). The ruled lines are
 * `display: none` on screen — they are a paper affordance, and drawing them on
 * screen would invite signing the glass.
 */
function SignatureBlock({
  countRunId,
  signatures,
  signerName,
  onSignerName,
  confirming,
  busy,
  onStart,
  onCancel,
  onConfirm,
}: {
  countRunId: string;
  signatures: SignaturesState;
  signerName: string;
  onSignerName: (value: string) => void;
  confirming: boolean;
  busy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const headingId = useId();
  return (
    <section className="decides-sign card" aria-labelledby={headingId}>
      <h2 className="decides-sign-heading" id={headingId}>
        Signatures on this result
      </h2>
      {signatures.step === 'reading' ? (
        <p className="decides-sign-transient" role="status">
          Reading who has signed…
        </p>
      ) : null}
      {signatures.step === 'unread' ? (
        <p className="decides-sign-transient">
          Who has already signed could not be read. You may still sign — the list is a courtesy, and
          a second signature of the same result is lawful.
        </p>
      ) : null}
      {signatures.step === 'read' ? (
        signatures.signatures.length === 0 ? (
          <p className="decides-sign-empty">Nobody has signed this result yet.</p>
        ) : (
          <ul className="decides-sign-list">
            {signatures.signatures.map((signature, index) => (
              <li key={`${signature.acceptedAt}-${String(index)}`}>
                Signed by {signature.signerName} at {formatNzInstant(signature.acceptedAt)}
              </li>
            ))}
          </ul>
        )
      ) : null}
      <SignForm
        countRunId={countRunId}
        signerName={signerName}
        onSignerName={onSignerName}
        confirming={confirming}
        busy={busy}
        onStart={onStart}
        onCancel={onCancel}
        onConfirm={onConfirm}
      />
      <button
        type="button"
        className="btn btn-ghost decides-sign-print"
        onClick={() => window.print()}
      >
        Print this result
      </button>
      {/* The countersign lines, paper only: a ruled line each for the
          Scrutineer and the President of Synod, name and date beneath — the
          printed sheet is handed over and countersigned in the hall. */}
      <div className="decides-sign-lines">
        <div className="decides-sign-line">
          <div className="decides-sign-rule" />
          <p className="decides-sign-line-label">Scrutineer</p>
          <p className="decides-sign-line-meta">
            Name: <span className="decides-sign-meta-rule" /> Date:{' '}
            <span className="decides-sign-meta-rule" />
          </p>
        </div>
        <div className="decides-sign-line">
          <div className="decides-sign-rule" />
          <p className="decides-sign-line-label">President of Synod</p>
          <p className="decides-sign-line-meta">
            Name: <span className="decides-sign-meta-rule" /> Date:{' '}
            <span className="decides-sign-meta-rule" />
          </p>
        </div>
      </div>
    </section>
  );
}

/**
 * The signing form, in the estate's APPROVER idiom (vetting's queue, not its
 * consent form): an official types their FULL NAME, no extra tick — the button
 * is the act — and the attestation sentence is FIXED COPY beside the field,
 * described to the input for assistive technology. The sentence rendered here
 * and the `signatureText` sent are the SAME call over the same run id
 * ({@link attestationSentence}), so what was shown is what is stored. Then the
 * DeclareForm's own two-tap shape: the primary is disabled until a name is
 * entered (the empty case never reaches the server's `missing_signature`), and
 * the confirm replaces it, Cancel first, with the field locked while it stands.
 *
 * `autoComplete="name"` where the President's field says "off": this field
 * collects the SIGNER'S OWN name, which WCAG 2.2 SC 1.3.5 asks be identified;
 * the declare field collects somebody else's.
 */
function SignForm({
  countRunId,
  signerName,
  onSignerName,
  confirming,
  busy,
  onStart,
  onCancel,
  onConfirm,
}: {
  countRunId: string;
  signerName: string;
  onSignerName: (value: string) => void;
  confirming: boolean;
  busy: boolean;
  onStart: () => void;
  onCancel: () => void;
  onConfirm: () => void;
}): JSX.Element {
  const inputId = useId();
  const sentenceId = useId();
  return (
    <div className="decides-sign-form">
      <label className="decides-scrutineer-label" htmlFor={inputId}>
        Your full name
      </label>
      <input
        id={inputId}
        className="decides-scrutineer-input"
        type="text"
        value={signerName}
        onChange={(event) => onSignerName(event.target.value)}
        disabled={confirming || busy}
        autoComplete="name"
        aria-describedby={sentenceId}
      />
      <p className="decides-sign-attestation" id={sentenceId}>
        {attestationSentence(countRunId)}
      </p>
      {confirming ? (
        <ConfirmBlock
          prompt={CONFIRM_COPY.sign.prompt}
          confirmLabel={CONFIRM_COPY.sign.confirmLabel}
          busy={busy}
          onConfirm={onConfirm}
          onCancel={onCancel}
        />
      ) : (
        <button
          type="button"
          className="btn btn-primary decides-scrutineer-primary"
          onClick={onStart}
          disabled={signerName.trim() === '' || busy}
        >
          Sign this result
        </button>
      )}
    </div>
  );
}
